import React, { useMemo, forwardRef } from 'react';
import { Card, Box, Typography } from '@mui/material';
import { Line } from 'react-chartjs-2';
import { BASE_OPTIONS, EJE_X_BASE, EJE_Y_BASE } from './chartBase';
import { COLOR, FUENTE } from './tokens';
import PeriodoUnicoKpi from './PeriodoUnicoKpi';
import InfoGrafico from './InfoGrafico';
import { MOCK_TENDENCIA_MENSUAL } from './mockDataEjemplos';

const NOMBRES_MES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

/**
 * Ausentismo del mes. Lo calcula el backend con la misma regla que el KPI del encabezado (turnos CON
 * ya resueltos, sin cancelados; los sobreturnos cuentan) y acá se muestra tal cual. Si el dato no viene
 * (los ejemplos de la ayuda) se calcula con los mismos conteos, sin inventar otra base.
 */
const ausentismoDelMes = (m) => {
    if (m.tasaAusentismo != null) return Number(m.tasaAusentismo);
    if (m.tasaAusentismo === null) return null; // el backend dice "sin turnos resueltos"
    const noVinieron = m.noAsistio ?? 0;
    const base = (m.atendidos ?? 0) + noVinieron;
    return base > 0 ? Math.round((noVinieron / base) * 1000) / 10 : null;
};

/**
 * Tendencia mensual de turnos y de ausentismo. Dos gráficos de una sola escala cada uno — nunca un eje
 * doble en el mismo gráfico — porque turnos (cientos) y ausentismo (%) no comparten magnitud. Con un
 * solo mes cargado no hay nada que comparar en una línea, así que se muestra como cifra simple (KPI).
 *
 * Un mes es PARCIAL (`parcial`, lo decide el backend) cuando el período pedido lo corta o el sistema
 * todavía no tenía turnos en todo el mes. Se marca con "*" y no entra en la tendencia de turnos ni en la
 * proyección: comparar 12 días contra 20 días, o contra un mes entero, da un cambio que no existe. El
 * ausentismo sí se compara: es una tasa y no depende de cuántos días tenga el mes.
 * @param {{data: Array<{anio: number, mes: number, total: number, tasaAusentismo?: number|null,
 *   parcial?: boolean}>}} props
 */
const TendenciaMensual = forwardRef(({ data, compacto = false }, ref) => {
    const filas = useMemo(() => (data || []).map((m) => ({
        ...m,
        label: `${NOMBRES_MES[m.mes - 1]} ${m.anio}${m.parcial ? '*' : ''}`,
        ausentismoPct: ausentismoDelMes(m),
    })), [data]);

    const hayComparacion = filas.length >= 2;
    const hayParciales = filas.some((f) => f.parcial);

    // Sin esto el eje de ausentismo autoescala a la banda mínima que cubre los datos
    // (ej. 7.6%–8.5%) y una diferencia de medio punto se ve como un quiebre enorme.
    // Se fuerza un piso de al menos 5 puntos de rango para que el eje sea legible.
    const { y1Min, y1Max } = useMemo(() => {
        const valores = filas.map((f) => f.ausentismoPct).filter((v) => v != null);
        if (!valores.length) return { y1Min: 0, y1Max: 10 };
        const min = Math.min(...valores);
        const max = Math.max(...valores);
        const centro = (min + max) / 2;
        const rango = Math.max(5, max - min + 2);
        return {
            y1Min: Math.max(0, Math.floor(centro - rango / 2)),
            y1Max: Math.ceil(centro + rango / 2),
        };
    }, [filas]);

    const { tendenciaTurnosPct, tendenciaAusentismoPts, proyeccion } = useMemo(() => {
        if (filas.length < 2) return { tendenciaTurnosPct: null, tendenciaAusentismoPts: null, proyeccion: null };
        const primero = filas[0];
        const ultimo = filas[filas.length - 1];
        const ausentismoPts = primero.ausentismoPct != null && ultimo.ausentismoPct != null
            ? ultimo.ausentismoPct - primero.ausentismoPct : null;
        // Los turnos solo se comparan entre meses completos: la variación de un mes parcial es un artefacto.
        if (filas.some((f) => f.parcial)) {
            return { tendenciaTurnosPct: null, tendenciaAusentismoPts: ausentismoPts, proyeccion: null };
        }
        const turnosPct = primero.total ? ((ultimo.total - primero.total) / primero.total) * 100 : null;
        // Proyección lineal simple a partir de la variación promedio mes a mes.
        const variaciones = filas.slice(1).map((f, i) => f.total - filas[i].total);
        const variacionPromedio = variaciones.reduce((a, b) => a + b, 0) / variaciones.length;
        return { tendenciaTurnosPct: turnosPct, tendenciaAusentismoPts: ausentismoPts, proyeccion: Math.max(0, Math.round(ultimo.total + variacionPromedio)) };
    }, [filas]);

    const chartDataTurnos = {
        labels: filas.map((f) => f.label),
        datasets: [{
            label: 'Turnos', data: filas.map((f) => f.total), borderColor: COLOR.azul,
            backgroundColor: COLOR.azul, pointRadius: 4, pointHoverRadius: 6, pointBorderColor: '#fff', pointBorderWidth: 1.5,
            tension: 0,
        }],
    };
    const optionsTurnos = {
        ...BASE_OPTIONS,
        plugins: { ...BASE_OPTIONS.plugins, legend: { display: false } },
        scales: {
            x: { ...EJE_X_BASE },
            y: { ...EJE_Y_BASE, title: { display: true, text: 'turnos', color: COLOR.grisTexto, font: { family: FUENTE, size: 11 } } },
        },
    };

    const chartDataAusentismo = {
        labels: filas.map((f) => f.label),
        datasets: [{
            label: 'Ausentismo %', data: filas.map((f) => f.ausentismoPct), borderColor: COLOR.ambar,
            backgroundColor: COLOR.ambar, pointRadius: 4, pointHoverRadius: 6, pointBorderColor: '#fff', pointBorderWidth: 1.5,
            tension: 0,
        }],
    };
    const optionsAusentismo = {
        ...BASE_OPTIONS,
        plugins: { ...BASE_OPTIONS.plugins, legend: { display: false } },
        scales: {
            x: { ...EJE_X_BASE },
            y: {
                ...EJE_Y_BASE, min: y1Min, max: y1Max,
                ticks: { ...EJE_Y_BASE.ticks, maxTicksLimit: 6, callback: (v) => `${Number(v).toFixed(1)}%` },
                title: { display: true, text: 'ausentismo', color: COLOR.grisTexto, font: { family: FUENTE, size: 11 } },
            },
        },
    };

    return (
        <Card variant="outlined" sx={{
            position: compacto ? 'static' : 'relative', border: compacto ? 'none' : '1px solid #e2e8f0',
            borderRadius: compacto ? 0 : '12px', boxShadow: 'none', padding: compacto ? 0 : '20px 22px',
        }}>
            {!compacto && (
                <>
                    <InfoGrafico
                        titulo="Tendencia mensual"
                        queMuestra="Te dice si el volumen de turnos y el ausentismo van mejorando o empeorando mes a mes — no solo el número de este mes, sino hacia dónde viene la curva."
                        ejemploVisual={<TendenciaMensual compacto data={MOCK_TENDENCIA_MENSUAL} />}
                        comoLeerlo={[
                            'Con menos de 2 meses de datos, cada métrica se muestra como una cifra simple (una sola línea no tiene nada con qué compararse).',
                            'Con 2 o más meses, cada métrica va en su propio gráfico de una sola escala — nunca turnos y % de ausentismo comparten el mismo eje, porque son magnitudes distintas.',
                            'El gráfico de "Turnos" (azul) es la cantidad total de turnos de cada mes.',
                            'El gráfico de "Ausentismo" (ámbar) es el % de no-presentismo de cada mes, con la misma regla que el encabezado: sobre los turnos que se resolvieron (sin cancelados). Su eje se ajusta a un rango mínimo de 5 puntos para que una diferencia real no se vea aplastada ni exagerada.',
                            'Un mes marcado con "*" es parcial: el período lo corta, o el sistema todavía no tenía turnos en todo el mes. Con un mes parcial no se calcula la tendencia de turnos ni la proyección (compararlo contra un mes entero, o contra otro parcial de distinto largo, inventa un cambio); el ausentismo sí, porque es una tasa.',
                            'Abajo, "Tendencia de turnos"/"de ausentismo" comparan el primer mes contra el último del rango (verde = mejora, rojo = empeora); "Proyección" es una estimación lineal simple del próximo mes.',
                        ]}
                        analisis="Los turnos crecen levemente (+5.0% de junio a agosto), lo que a primera vista se ve bien — pero el ausentismo sube de 10.5% a 15.4% en el mismo período (+4.9 puntos). Mirando solo el volumen de turnos, este trimestre parece estable; mirando el ausentismo, se nota que una parte creciente de ese volumen no se está aprovechando. Conviene revisar qué cambió en julio/agosto (¿un canal nuevo?, ¿se dejó de mandar el recordatorio?) antes de que la tendencia siga empeorando."
                    />
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 16, fontWeight: 700, color: COLOR.negro }}>Tendencia mensual</Typography>
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 12.5, color: COLOR.grisTexto, marginBottom: '14px' }}>
                        Turnos totales y % de ausentismo, mes a mes.
                    </Typography>
                </>
            )}
            {!hayComparacion ? (
                <PeriodoUnicoKpi items={[
                    { etiqueta: 'Turnos del mes', valor: filas[0] ? filas[0].total.toLocaleString('es-AR') : '—' },
                    {
                        etiqueta: 'Ausentismo del mes',
                        valor: filas[0] && filas[0].ausentismoPct != null ? `${filas[0].ausentismoPct.toFixed(1)}%` : '—',
                        color: COLOR.ambar,
                    },
                ]} />
            ) : (
                <>
                    <Box sx={{ display: 'grid', gridTemplateColumns: compacto ? 'minmax(0, 1fr) minmax(0, 1fr)' : { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 1fr)' }, gap: compacto ? '8px' : '16px' }}>
                        <Box>
                            {!compacto && <Typography sx={{ fontFamily: FUENTE, fontSize: 12, fontWeight: 700, color: COLOR.azul, marginBottom: '4px' }}>Turnos</Typography>}
                            <Box sx={{ height: compacto ? 130 : 220 }}><Line ref={ref} data={chartDataTurnos} options={optionsTurnos} /></Box>
                        </Box>
                        <Box>
                            {!compacto && <Typography sx={{ fontFamily: FUENTE, fontSize: 12, fontWeight: 700, color: COLOR.ambar, marginBottom: '4px' }}>Ausentismo</Typography>}
                            <Box sx={{ height: compacto ? 130 : 220 }}><Line data={chartDataAusentismo} options={optionsAusentismo} /></Box>
                        </Box>
                    </Box>
                    {!compacto && hayParciales && (
                        <Typography sx={{ fontFamily: FUENTE, fontSize: 11.5, color: COLOR.grisTexto, marginTop: '10px' }}>
                            * Mes parcial (el período lo corta o el sistema todavía no tenía turnos todo el mes): por eso no se calcula la
                            tendencia de turnos ni la proyección. El ausentismo sí, porque es una tasa.
                        </Typography>
                    )}
                    {!compacto && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' }, gap: '12px', marginTop: '18px' }}>
                            <Resumen etiqueta="Tendencia de turnos"
                                valor={tendenciaTurnosPct != null ? `${tendenciaTurnosPct > 0 ? '+' : ''}${tendenciaTurnosPct.toFixed(1)}%` : '—'}
                                color={tendenciaTurnosPct == null ? COLOR.negro : tendenciaTurnosPct >= 0 ? COLOR.verde : COLOR.rojo} />
                            <Resumen etiqueta="Tendencia de ausentismo"
                                valor={tendenciaAusentismoPts != null ? `${tendenciaAusentismoPts > 0 ? '+' : ''}${tendenciaAusentismoPts.toFixed(1)} pts` : '—'}
                                color={tendenciaAusentismoPts == null ? COLOR.negro : tendenciaAusentismoPts <= 0 ? COLOR.verde : COLOR.rojo} />
                            <Resumen etiqueta="Proyección próximo mes" valor={proyeccion != null ? `~${proyeccion}` : '—'} color={COLOR.negro} />
                        </Box>
                    )}
                </>
            )}
        </Card>
    );
});

const Resumen = ({ etiqueta, valor, color }) => (
    <Box sx={{ background: '#F8FAFC', border: '1px solid #F1F5F9', borderRadius: '8px', padding: '10px 12px' }}>
        <Typography sx={{ fontFamily: FUENTE, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: COLOR.grisTexto }}>
            {etiqueta}
        </Typography>
        <Typography sx={{ fontFamily: FUENTE, fontSize: 18, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>
            {valor}
        </Typography>
    </Box>
);

export default TendenciaMensual;
